// src/components/CartRecommendation.js
// One personalised "you might also like" suggestion in the cart drawer
// (roadmap 2.4). Picks the first in-stock scent not already in the cart from:
//   1. the visitor's scent quiz matches (Best, then Alternative)
//   2. the partner of the scent most recently added (data/scentProfiles.js)
//   3. any other scent, best seller first
// Shows nothing if every scent is already in the cart.

import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getScent, BEST_SELLER_HANDLE } from '../data/scents';
import { SCENT_PROFILES } from '../data/scentProfiles';
import { getQuizResult } from '../lib/analytics';

const ALL_HANDLES = Object.keys(SCENT_PROFILES);

// Ordered list of { handle, reason } to try, before stock is checked.
function candidates(lines) {
    const inCart = new Set(lines.map(l => l.handle));
    const list = [];
    const push = (handle, reason) => {
        if (handle && SCENT_PROFILES[handle] && !inCart.has(handle) && !list.some(c => c.handle === handle)) {
            list.push({ handle, reason });
        }
    };

    const quiz = getQuizResult();
    push(quiz?.bestHandle, 'Your quiz Best Match');
    push(quiz?.alternativeHandle, 'Your quiz Alternative Match');

    // Cart lines are in the order they were added, so the last scent is the newest.
    const lastScent = [...lines].reverse().find(l => SCENT_PROFILES[l.handle]);
    if (lastScent) push(SCENT_PROFILES[lastScent.handle].partner, `Pairs well with ${lastScent.title}`);

    push(BEST_SELLER_HANDLE, 'Our best seller');
    ALL_HANDLES.forEach(handle => push(handle, 'You might also like'));
    return list;
}

const CartRecommendation = ({ lines, country, symbol }) => {
    const { addToCart, loading } = useCart();
    const [pick, setPick] = useState(null);

    // Re-run when the cart's contents or currency change. Keyed on handles
    // rather than the lines array so quantity changes don't trigger a refetch.
    const cartKey = lines.map(l => l.handle).join(',');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            for (const { handle, reason } of candidates(lines)) {
                try {
                    const product = await getScent(handle, country);
                    if (product?.availableForSale && product.variantId) {
                        if (!cancelled) setPick({ product, reason });
                        return;
                    }
                } catch {
                    // Lookup failed — try the next candidate
                }
                if (cancelled) return;
            }
            if (!cancelled) setPick(null);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [cartKey, country]);

    if (!pick) return null;
    const { product, reason } = pick;

    return (
        <div className="rounded-xl border border-[#CEAC8F]/40 bg-[#CEAC8F]/5 p-4 flex items-center gap-4">
            <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                <img src={product.image} alt={product.imageAlt || product.name} className="w-full h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[#CEAC8F] text-[10px] tracking-widest uppercase mb-1 truncate">{reason}</p>
                <p className="text-foreground text-sm font-medium truncate">{product.name}</p>
                <p className="text-muted-foreground text-xs">
                    {SCENT_PROFILES[product.handle]?.profile} · {symbol}{product.price.toFixed(2)}
                </p>
            </div>
            <button
                onClick={() => addToCart(product.variantId)}
                disabled={loading}
                aria-label={`Add ${product.name} to cart`}
                className="flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg bg-[#CEAC8F] text-black text-sm font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60"
            >
                <Plus className="w-4 h-4" /> Add
            </button>
        </div>
    );
};

export default CartRecommendation;
